import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Button, Stack, alpha, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BooleanField from '../../booleanField';
import JsonField from '../../jsonField';
import RadioGroup from '../../raidioGroup';
import SelectField from '../../selectField';
import TextField from '../../textField';

type Props = {
  questions: Record<string, any>;
  userResponses: Record<string, any>;
  maxHeight: number;
  dataSources: any[];
  onUserResponseChange: (questionId: string, response: any) => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
};

const StepDataSource = ({
  questions = {},
  userResponses = {},
  maxHeight,
  dataSources = [],
  onUserResponseChange,
  onBack,
  onCancel,
  onNext,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [containerRef, containerSize] = useElementSize();
  const [actionPanelRef, actionPanelSize] = useElementSize();

  const [dataSourceOptions, setDataSourceOptions] = useState<any[]>(
    dataSources.map((d) => ({ value: d.id, label: `${d.name} (${d.dataSourceDef?.name || 'Data Source'})` })),
  );

  const [errors, setErrors] = useState<any>({});

  const setDataSourceType = (response: string) => {
    onUserResponseChange('dataSourceType', response);
  };

  const setDataSourceSettings = (settingName: string, value: any) => {
    onUserResponseChange('dataSourceSettings', { ...userResponses.dataSourceSettings, [settingName]: value });
  };

  const setDataSource = (id: string) => {
    const dataSource = dataSources.find((d) => d.id === id);
    onUserResponseChange('existingDataSource', {
      id: id,
      dataSourceDefId: dataSource?.entityDefId || '',
      name: dataSource?.name || 'Existing Data Source',
    });
  };

  const handleNext = () => {
    const errors: any = {};
    let hasError = false;
    if (!userResponses.dataSourceType) {
      errors.dataSourceType = t('common.errors.requiredField');
      hasError = true;
    }
    if (userResponses.dataSourceType === 'existingDatasource') {
      if (!userResponses.existingDataSource) {
        errors.existingDataSource = t('common.errors.requiredField');
        hasError = true;
      }
    }
    if (userResponses.dataSourceType === 'azureBlob') {
      if (!userResponses.dataSourceSettings?.AZURE_BLOB_ACCOUNT_NAME) {
        errors.AZURE_BLOB_ACCOUNT_NAME = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.AZURE_BLOB_CONTAINER_NAME) {
        errors.AZURE_BLOB_CONTAINER_NAME = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.AZURE_BLOB_ACCOUNT_KEY) {
        errors.AZURE_BLOB_ACCOUNT_KEY = t('common.errors.requiredField');
        hasError = true;
      }
    }
    if (userResponses.dataSourceType === 'awsS3') {
      if (!userResponses.dataSourceSettings?.S3_BUCKET_NAME_OR_ARN) {
        errors.S3_BUCKET_NAME_OR_ARN = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.S3_REGION) {
        errors.S3_REGION = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.S3_ACCESS_KEY) {
        errors.S3_ACCESS_KEY = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.S3_SECRET_KEY) {
        errors.S3_SECRET_KEY = t('common.errors.requiredField');
        hasError = true;
      }
    }

    if (userResponses.dataSourceType === 'mqttBroker') {
      if (!userResponses.dataSourceSettings?.MQTT_BROKER_URL) {
        errors.MQTT_BROKER_URL = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.MQTT_TOPIC) {
        errors.MQTT_TOPIC = t('common.errors.requiredField');
        hasError = true;
      }
    }

    if (userResponses.dataSourceType === 'influxdb') {
      if (!userResponses.dataSourceSettings?.INFLUXDB_URL) {
        errors.INFLUXDB_URL = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.INFLUXDB_TOKEN) {
        errors.INFLUXDB_TOKEN = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.INFLUXDB_ORG) {
        errors.INFLUXDB_ORG = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.INFLUXDB_BUCKET) {
        errors.INFLUXDB_BUCKET = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.INFLUXDB_FLUX_QUERY) {
        errors.INFLUXDB_FLUX_QUERY = t('common.errors.requiredField');
        hasError = true;
      }
    }

    if (userResponses.dataSourceType === 'kafkaTopic') {
      if (!userResponses.dataSourceSettings?.KAFKA_BROKERS) {
        errors.KAFKA_BROKERS = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.KAFKA_TOPIC) {
        errors.KAFKA_TOPIC = t('common.errors.requiredField');
        hasError = true;
      }
    }

    if (userResponses.dataSourceType === 'opcuaServer') {
      if (!userResponses.dataSourceSettings?.OPCUA_ENDPOINT_URL) {
        errors.OPCUA_ENDPOINT_URL = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.OPCUA_NODE_ID) {
        errors.OPCUA_NODE_ID = t('common.errors.requiredField');
        hasError = true;
      }
    }

    if (userResponses.dataSourceType === 'restApiPoller') {
      if (!userResponses.dataSourceSettings?.REST_API_URL) {
        errors.REST_API_URL = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.SCHEDULING_POLL_INTERVAL) {
        errors.SCHEDULING_POLL_INTERVAL = t('common.errors.requiredField');
        hasError = true;
      }
    }

    if (userResponses.dataSourceType === 'googledrive') {
      if (userResponses.dataSourceSettings?.GOOGLE_DRIVE_USE_SERVICE_ACCOUNT) {
        if (!userResponses.dataSourceSettings?.GOOGLE_DRIVE_SERVICE_ACCOUNT_KEY) {
          errors.GOOGLE_DRIVE_SERVICE_ACCOUNT_KEY = t('common.errors.requiredField');
          hasError = true;
        }
      } else {
        if (!userResponses.dataSourceSettings?.GOOGLE_DRIVE_CLIENT_ID) {
          errors.GOOGLE_DRIVE_CLIENT_ID = t('common.errors.requiredField');
          hasError = true;
        }
        if (!userResponses.dataSourceSettings?.GOOGLE_DRIVE_CLIENT_SECRET) {
          errors.GOOGLE_DRIVE_CLIENT_SECRET = t('common.errors.requiredField');
          hasError = true;
        }
      }
    }

    if (userResponses.dataSourceType === 'mssite') {
      if (!userResponses.dataSourceSettings?.DATA_SOURCE_MS_SITE_SITE_ID) {
        errors.DATA_SOURCE_MS_SITE_SITE_ID = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_TENANT_ID) {
        errors.DATA_SOURCE_MS_SITE_AUTH_TENANT_ID = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_CLIENT_ID) {
        errors.DATA_SOURCE_MS_SITE_AUTH_CLIENT_ID = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_CLIENT_SECRET) {
        errors.DATA_SOURCE_MS_SITE_AUTH_CLIENT_SECRET = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_SCOPE) {
        errors.DATA_SOURCE_MS_SITE_AUTH_SCOPE = t('common.errors.requiredField');
        hasError = true;
      }
    }
    if (userResponses.dataSourceType === 'sql') {
      if (!userResponses.dataSourceSettings?.dbType) {
        errors.dbType = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.host) {
        errors.host = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.dbName) {
        errors.dbName = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.username) {
        errors.username = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.password) {
        errors.password = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.dataSourceSettings?.sqlScript) {
        errors.sqlScript = t('common.errors.requiredField');
        hasError = true;
      }
    }

    setErrors(errors);
    if (!hasError) {
      onNext();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleBack = () => {
    onBack();
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: `${maxHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack alignItems={'flex-start'} spacing={1}>
          <RadioGroup
            label={questions?.dataSourceType?.question || 'From where do you want us to retrieve your input files / data?'}
            value={userResponses.dataSourceType || 'userUpload'}
            options={
              questions?.dataSourceType?.options || [
                { label: 'I will upload files here', value: 'userUpload' },
                { label: 'I will use an existing data source', value: 'existingDatasource' },
                { label: 'Azure Blob Storage', value: 'azureBlob' },
                { label: 'AWS S3 Bucket', value: 'awsS3' },
                { label: 'Google Drive', value: 'googledrive' },
                { label: 'Microsoft SharePoint', value: 'mssite' },
                { label: 'SQL Database', value: 'sql' },
                { label: 'MQTT Broker', value: 'mqttBroker' },
                { label: 'InfluxDB Time-Series', value: 'influxdb' },
                { label: 'Apache Kafka Topic', value: 'kafkaTopic' },
                { label: 'OPC-UA Server', value: 'opcuaServer' },
                { label: 'REST API Poller / Endpoint', value: 'restApiPoller' },
              ]
            }
            required
            error={errors.dataSourceType}
            onChange={setDataSourceType}
            maxOptionsPerColumn={4}
          />
          {userResponses.dataSourceType === 'azureBlob' && (
            <>
              <TextField
                label={questions?.dataSourceSettings?.AZURE_BLOB_ACCOUNT_NAME?.question || 'Azure Storage Account Name'}
                value={userResponses.dataSourceSettings?.AZURE_BLOB_ACCOUNT_NAME}
                required
                error={errors.AZURE_BLOB_ACCOUNT_NAME}
                onChange={(value) => setDataSourceSettings('AZURE_BLOB_ACCOUNT_NAME', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.AZURE_BLOB_CONTAINER_NAME?.question || 'Container Name'}
                value={userResponses.dataSourceSettings?.AZURE_BLOB_CONTAINER_NAME}
                required
                error={errors.AZURE_BLOB_CONTAINER_NAME}
                onChange={(value) => setDataSourceSettings('AZURE_BLOB_CONTAINER_NAME', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.AZURE_BLOB_ACCOUNT_KEY?.question || 'Account Key'}
                value={userResponses.dataSourceSettings?.AZURE_BLOB_ACCOUNT_KEY}
                required
                error={errors.AZURE_BLOB_ACCOUNT_KEY}
                onChange={(value) => setDataSourceSettings('AZURE_BLOB_ACCOUNT_KEY', value)}
                type="password"
                fullWidth={false}
              />
            </>
          )}
          {userResponses.dataSourceType === 'awsS3' && (
            <>
              <TextField
                label={questions?.dataSourceSettings?.S3_BUCKET_NAME_OR_ARN?.question || 'S3 Bucket Name or ARN'}
                value={userResponses.dataSourceSettings?.S3_BUCKET_NAME_OR_ARN}
                required
                error={errors.S3_BUCKET_NAME_OR_ARN}
                onChange={(value) => setDataSourceSettings('S3_BUCKET_NAME_OR_ARN', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.S3_REGION?.question || 'AWS Region'}
                value={userResponses.dataSourceSettings?.S3_REGION}
                required
                error={errors.S3_REGION}
                onChange={(value) => setDataSourceSettings('S3_REGION', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.S3_ACCESS_KEY?.question || 'Access Key'}
                value={userResponses.dataSourceSettings?.S3_ACCESS_KEY}
                required
                error={errors.S3_ACCESS_KEY}
                onChange={(value) => setDataSourceSettings('S3_ACCESS_KEY', value)}
                type="password"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.S3_SECRET_KEY?.question || 'Secret Key'}
                value={userResponses.dataSourceSettings?.S3_SECRET_KEY}
                required
                error={errors.S3_SECRET_KEY}
                onChange={(value) => setDataSourceSettings('S3_SECRET_KEY', value)}
                type="password"
                fullWidth={false}
              />
            </>
          )}

          {userResponses.dataSourceType === 'mqttBroker' && (
            <>
              <TextField
                label={questions?.dataSourceSettings?.MQTT_BROKER_URL?.question || 'MQTT Broker URL'}
                value={userResponses.dataSourceSettings?.MQTT_BROKER_URL || ''}
                required
                error={errors.MQTT_BROKER_URL}
                onChange={(value) => setDataSourceSettings('MQTT_BROKER_URL', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.MQTT_TOPIC?.question || 'MQTT Topic'}
                value={userResponses.dataSourceSettings?.MQTT_TOPIC || ''}
                required
                error={errors.MQTT_TOPIC}
                onChange={(value) => setDataSourceSettings('MQTT_TOPIC', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.MQTT_CLIENT_ID?.question || 'MQTT Client ID'}
                value={userResponses.dataSourceSettings?.MQTT_CLIENT_ID || ''}
                error={errors.MQTT_CLIENT_ID}
                onChange={(value) => setDataSourceSettings('MQTT_CLIENT_ID', value)}
                type="text"
                fullWidth={false}
              />
            </>
          )}

          {userResponses.dataSourceType === 'influxdb' && (
            <>
              <TextField
                label={questions?.dataSourceSettings?.INFLUXDB_URL?.question || 'InfluxDB URL'}
                value={userResponses.dataSourceSettings?.INFLUXDB_URL || ''}
                required
                error={errors.INFLUXDB_URL}
                onChange={(value) => setDataSourceSettings('INFLUXDB_URL', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.INFLUXDB_TOKEN?.question || 'InfluxDB Token'}
                value={userResponses.dataSourceSettings?.INFLUXDB_TOKEN || ''}
                required
                error={errors.INFLUXDB_TOKEN}
                onChange={(value) => setDataSourceSettings('INFLUXDB_TOKEN', value)}
                type="password"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.INFLUXDB_ORG?.question || 'InfluxDB Org'}
                value={userResponses.dataSourceSettings?.INFLUXDB_ORG || ''}
                required
                error={errors.INFLUXDB_ORG}
                onChange={(value) => setDataSourceSettings('INFLUXDB_ORG', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.INFLUXDB_BUCKET?.question || 'InfluxDB Bucket'}
                value={userResponses.dataSourceSettings?.INFLUXDB_BUCKET || ''}
                required
                error={errors.INFLUXDB_BUCKET}
                onChange={(value) => setDataSourceSettings('INFLUXDB_BUCKET', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.INFLUXDB_FLUX_QUERY?.question || 'InfluxDB Flux Query'}
                value={userResponses.dataSourceSettings?.INFLUXDB_FLUX_QUERY || ''}
                required
                error={errors.INFLUXDB_FLUX_QUERY}
                onChange={(value) => setDataSourceSettings('INFLUXDB_FLUX_QUERY', value)}
                type="text"
                fullWidth={false}
                multiline
                rows={3}
              />
            </>
          )}

          {userResponses.dataSourceType === 'kafkaTopic' && (
            <>
              <TextField
                label={questions?.dataSourceSettings?.KAFKA_BROKERS?.question || 'Kafka Brokers (comma separated)'}
                value={userResponses.dataSourceSettings?.KAFKA_BROKERS || ''}
                required
                error={errors.KAFKA_BROKERS}
                onChange={(value) => setDataSourceSettings('KAFKA_BROKERS', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.KAFKA_TOPIC?.question || 'Kafka Topic'}
                value={userResponses.dataSourceSettings?.KAFKA_TOPIC || ''}
                required
                error={errors.KAFKA_TOPIC}
                onChange={(value) => setDataSourceSettings('KAFKA_TOPIC', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.KAFKA_GROUP_ID?.question || 'Kafka Consumer Group ID'}
                value={userResponses.dataSourceSettings?.KAFKA_GROUP_ID || ''}
                error={errors.KAFKA_GROUP_ID}
                onChange={(value) => setDataSourceSettings('KAFKA_GROUP_ID', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.KAFKA_USERNAME?.question || 'Kafka SASL Username'}
                value={userResponses.dataSourceSettings?.KAFKA_USERNAME || ''}
                error={errors.KAFKA_USERNAME}
                onChange={(value) => setDataSourceSettings('KAFKA_USERNAME', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.KAFKA_PASSWORD?.question || 'Kafka SASL Password'}
                value={userResponses.dataSourceSettings?.KAFKA_PASSWORD || ''}
                error={errors.KAFKA_PASSWORD}
                onChange={(value) => setDataSourceSettings('KAFKA_PASSWORD', value)}
                type="password"
                fullWidth={false}
              />
            </>
          )}

          {userResponses.dataSourceType === 'opcuaServer' && (
            <>
              <TextField
                label={questions?.dataSourceSettings?.OPCUA_ENDPOINT_URL?.question || 'OPC-UA Endpoint URL'}
                value={userResponses.dataSourceSettings?.OPCUA_ENDPOINT_URL || ''}
                required
                error={errors.OPCUA_ENDPOINT_URL}
                onChange={(value) => setDataSourceSettings('OPCUA_ENDPOINT_URL', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.OPCUA_NODE_ID?.question || 'OPC-UA Node ID'}
                value={userResponses.dataSourceSettings?.OPCUA_NODE_ID || ''}
                required
                error={errors.OPCUA_NODE_ID}
                onChange={(value) => setDataSourceSettings('OPCUA_NODE_ID', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.OPCUA_USERNAME?.question || 'OPC-UA Username'}
                value={userResponses.dataSourceSettings?.OPCUA_USERNAME || ''}
                error={errors.OPCUA_USERNAME}
                onChange={(value) => setDataSourceSettings('OPCUA_USERNAME', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.OPCUA_PASSWORD?.question || 'OPC-UA Password'}
                value={userResponses.dataSourceSettings?.OPCUA_PASSWORD || ''}
                error={errors.OPCUA_PASSWORD}
                onChange={(value) => setDataSourceSettings('OPCUA_PASSWORD', value)}
                type="password"
                fullWidth={false}
              />
            </>
          )}

          {userResponses.dataSourceType === 'restApiPoller' && (
            <>
              <TextField
                label={questions?.dataSourceSettings?.REST_API_URL?.question || 'REST API URL'}
                value={userResponses.dataSourceSettings?.REST_API_URL || ''}
                required
                error={errors.REST_API_URL}
                onChange={(value) => setDataSourceSettings('REST_API_URL', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.SCHEDULING_POLL_INTERVAL?.question || 'Poll Interval (minutes)'}
                value={userResponses.dataSourceSettings?.SCHEDULING_POLL_INTERVAL || ''}
                required
                error={errors.SCHEDULING_POLL_INTERVAL}
                onChange={(value) => setDataSourceSettings('SCHEDULING_POLL_INTERVAL', value)}
                type="number"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.REST_API_METHOD?.question || 'HTTP Method (GET/POST)'}
                value={userResponses.dataSourceSettings?.REST_API_METHOD || ''}
                error={errors.REST_API_METHOD}
                onChange={(value) => setDataSourceSettings('REST_API_METHOD', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.REST_API_HEADERS?.question || 'HTTP Headers (JSON format)'}
                value={userResponses.dataSourceSettings?.REST_API_HEADERS || ''}
                error={errors.REST_API_HEADERS}
                onChange={(value) => setDataSourceSettings('REST_API_HEADERS', value)}
                type="text"
                fullWidth={false}
              />
            </>
          )}

          {userResponses.dataSourceType === 'googledrive' && (
            <>
              <BooleanField
                label={
                  questions?.dataSourceSettings?.GOOGLE_DRIVE_USE_SERVICE_ACCOUNT?.question || 'Use Service Account JSON'
                }
                value={userResponses.dataSourceSettings?.GOOGLE_DRIVE_USE_SERVICE_ACCOUNT || false}
                onChange={(value) => setDataSourceSettings('GOOGLE_DRIVE_USE_SERVICE_ACCOUNT', value)}
                error={errors.GOOGLE_DRIVE_USE_SERVICE_ACCOUNT}
              />

              {userResponses.dataSourceSettings?.GOOGLE_DRIVE_USE_SERVICE_ACCOUNT ? (
                <JsonField
                  label={
                    questions?.dataSourceSettings?.GOOGLE_DRIVE_SERVICE_ACCOUNT_KEY?.question ||
                    'Google account credential json'
                  }
                  value={userResponses.dataSourceSettings?.GOOGLE_DRIVE_SERVICE_ACCOUNT_KEY}
                  required
                  error={errors.GOOGLE_DRIVE_SERVICE_ACCOUNT_KEY}
                  onChange={(value) => setDataSourceSettings('GOOGLE_DRIVE_SERVICE_ACCOUNT_KEY', value)}
                />
              ) : (
                <>
                  <TextField
                    label={questions?.dataSourceSettings?.GOOGLE_DRIVE_CLIENT_ID?.question || 'Client id'}
                    value={userResponses.dataSourceSettings?.GOOGLE_DRIVE_CLIENT_ID}
                    required
                    error={errors.GOOGLE_DRIVE_CLIENT_ID}
                    onChange={(value) => setDataSourceSettings('GOOGLE_DRIVE_CLIENT_ID', value)}
                    type="text"
                    fullWidth={false}
                  />
                  <TextField
                    label={questions?.dataSourceSettings?.GOOGLE_DRIVE_CLIENT_SECRET?.question || 'Client secret'}
                    value={userResponses.dataSourceSettings?.GOOGLE_DRIVE_CLIENT_SECRET}
                    required
                    error={errors.GOOGLE_DRIVE_CLIENT_SECRET}
                    onChange={(value) => setDataSourceSettings('GOOGLE_DRIVE_CLIENT_SECRET', value)}
                    type="password"
                    fullWidth={false}
                  />
                  <TextField
                    label={questions?.dataSourceSettings?.GOOGLE_DRIVE_REFRESH_TOKEN?.question || 'Refresh token'}
                    value={userResponses.dataSourceSettings?.GOOGLE_DRIVE_REFRESH_TOKEN}
                    onChange={(value) => setDataSourceSettings('GOOGLE_DRIVE_REFRESH_TOKEN', value)}
                    type="password"
                    fullWidth={false}
                  />
                </>
              )}
              <TextField
                label={questions?.dataSourceSettings?.GOOGLE_DRIVE_FOLDER_ID?.question || 'Folder id'}
                value={userResponses.dataSourceSettings?.GOOGLE_DRIVE_FOLDER_ID}
                onChange={(value) => setDataSourceSettings('GOOGLE_DRIVE_FOLDER_ID', value)}
                type="text"
                fullWidth={false}
              />
            </>
          )}

          {userResponses.dataSourceType === 'mssite' && (
            <>
              <TextField
                label={questions?.dataSourceSettings?.DATA_SOURCE_MS_SITE_SITE_ID?.question || 'SharePoint Site ID or Name'}
                value={userResponses.dataSourceSettings?.DATA_SOURCE_MS_SITE_SITE_ID}
                required
                error={errors.DATA_SOURCE_MS_SITE_SITE_ID}
                onChange={(value) => setDataSourceSettings('DATA_SOURCE_MS_SITE_SITE_ID', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.DATA_SOURCE_MS_SITE_BASE_PATH?.question || 'Base Path'}
                value={userResponses.dataSourceSettings?.DATA_SOURCE_MS_SITE_BASE_PATH}
                error={errors.DATA_SOURCE_MS_SITE_BASE_PATH}
                onChange={(value) => setDataSourceSettings('DATA_SOURCE_MS_SITE_BASE_PATH', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_TENANT_ID?.question || 'Authentication Tenant ID'}
                value={userResponses.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_TENANT_ID}
                required
                error={errors.DATA_SOURCE_MS_SITE_AUTH_TENANT_ID}
                onChange={(value) => setDataSourceSettings('DATA_SOURCE_MS_SITE_AUTH_TENANT_ID', value)}
                type="text"
                fullWidth={false}
              />

              <TextField
                label={questions?.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_CLIENT_ID?.question || 'Authentication Client ID'}
                value={userResponses.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_CLIENT_ID}
                required
                error={errors.DATA_SOURCE_MS_SITE_AUTH_CLIENT_ID}
                onChange={(value) => setDataSourceSettings('DATA_SOURCE_MS_SITE_AUTH_CLIENT_ID', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_CLIENT_SECRET?.question || 'Authentication Client Secret'}
                value={userResponses.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_CLIENT_SECRET}
                required
                error={errors.DATA_SOURCE_MS_SITE_AUTH_CLIENT_SECRET}
                onChange={(value) => setDataSourceSettings('DATA_SOURCE_MS_SITE_AUTH_CLIENT_SECRET', value)}
                type="password"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_SCOPE?.question || 'Authentication Scope'}
                value={userResponses.dataSourceSettings?.DATA_SOURCE_MS_SITE_AUTH_SCOPE}
                required
                error={errors.DATA_SOURCE_MS_SITE_AUTH_SCOPE}
                onChange={(value) => setDataSourceSettings('DATA_SOURCE_MS_SITE_AUTH_SCOPE', value)}
                type="text"
                fullWidth={false}
              />
            </>
          )}
          {userResponses.dataSourceType === 'sql' && (
            <>
              <SelectField
                label={questions?.dataSourceSettings?.dbType?.question || 'Database Engine'}
                value={userResponses.dataSourceSettings?.dbType || 'postgresql'}
                options={
                  questions?.dataSourceSettings?.dbType?.options || [
                    { label: 'PostgreSQL', value: 'postgresql' },
                    { label: 'MySQL / MariaDB', value: 'mysql' },
                    { label: 'MS SQL Server', value: 'mssql' },
                  ]
                }
                required
                error={errors.dbType}
                onChange={(value) => setDataSourceSettings('dbType', value)}
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.host?.question || 'Host / Server'}
                value={userResponses.dataSourceSettings?.host}
                required
                error={errors.host}
                onChange={(value) => setDataSourceSettings('host', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.port?.question || 'Port'}
                value={userResponses.dataSourceSettings?.port}
                error={errors.port}
                onChange={(value) => setDataSourceSettings('port', value)}
                type="number"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.dbName?.question || 'Database Name'}
                value={userResponses.dataSourceSettings?.dbName}
                required
                error={errors.dbName}
                onChange={(value) => setDataSourceSettings('dbName', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.username?.question || 'Username'}
                value={userResponses.dataSourceSettings?.username}
                required
                error={errors.username}
                onChange={(value) => setDataSourceSettings('username', value)}
                type="text"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.password?.question || 'Password'}
                value={userResponses.dataSourceSettings?.password}
                required
                error={errors.password}
                onChange={(value) => setDataSourceSettings('password', value)}
                type="password"
                fullWidth={false}
              />
              <TextField
                label={questions?.dataSourceSettings?.sqlScript?.question || 'SQL SELECT Script / Query'}
                value={userResponses.dataSourceSettings?.sqlScript}
                required
                error={errors.sqlScript}
                onChange={(value) => setDataSourceSettings('sqlScript', value)}
                type="text"
                fullWidth={false}
                multiline
                rows={4}
              />
            </>
          )}

          {userResponses.dataSourceType === 'existingDatasource' && (
            <SelectField
              label={questions?.dataSourceSettings?.existingDataSource?.question || 'Select an existing Data Source'}
              value={userResponses.existingDataSource?.id || ''}
              options={dataSourceOptions}
              required
              error={errors.existingDataSource}
              onChange={setDataSource}
              fullWidth={false}
            />
          )}
        </Stack>
      </Box>
      <Box
        ref={actionPanelRef}
        sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            {t('common.buttons.back')}
          </Button>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="text"
              onClick={handleCancel}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              {t('common.buttons.cancel')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              disabled={!userResponses.dataSourceType}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 2.5,
                color: 'text.primary',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {t('common.buttons.next')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default StepDataSource;
